import { useRef, useState } from 'react';
import omit from 'lodash/fp/omit';
import isEmpty from 'lodash/fp/isEmpty';
import classNames from 'classnames';
import TableViewToggles from '@rio-cloud/rio-uikit/TableViewToggles';
import { Dialog, TableViewTogglesViewType } from '@rio-cloud/rio-uikit';
import MapEvents from '../../shared/components/Maps';
import { useSelectVehicleStore } from '../../services/selectVehicleSlice';
import { ColumnDetailsMap } from './models/ColumnDetailsMap';
import { ColumnLabel } from './models/ColumnLabel';
import { useInfractionsData } from './hooks/useInfractionsData';
import { useTableSettings } from './hooks/useTableSettings';
import { useWindowResize } from './hooks/useWindowResize';
import DashboardTableToolbar from './components/DashboardTableToolbar';
import DashboardTableBody from './components/DashboardTableBody';
import { IInfractionResult } from './models/InfractionResult';
import style from './DashboardTableInfractions.module.css'

export type DashboardTableProps = {
  viewType: TableViewTogglesViewType;
  hasData: boolean;
  tabName: string;
  infractionName: string;
};
const disabledColumns: string[] = [];
const subRows = ['detailLastUpdate', 'detailMeasuredValue', 'detailConfiguredKilometers', 'latLong', 'detailCumKilometers', 'detailEngineHours', 'detailOccurrences', 'coordinates'];
const columnsDetailsDescriptor: ColumnDetailsMap = {
  occurrences: {
    width: 250
  },
  extraAction: {
    width: 50
  }
};

const DashboardTableInfractions = (props: DashboardTableProps) => {
  const { viewType: externalViewType, hasData, tabName, infractionName } = props;
  const { selectedVehicle } = useSelectVehicleStore();
  const columnLabels: ColumnLabel = {
    tripStart: 'Início da Trip',
    tripFinal: 'Final da Trip',
    cumKilometers: 'Km Rodados',
    engineHours: 'Horas Motor',
    occurrences: `Ocorrências de ${tabName}`,
    configKilometers: 'Valor Configurado',
    measuredValue: 'Maior Valor medido(excesso)',
    detailLastUpdate: 'Última Atualização Detalhes',
    coordinates: 'Latitude e Longitude',
    // detailCumKilometers: '',
    // detailEngineHours: '',
    // detailOccurrences: '',
    detailConfiguredKilometers: 'Valor Configurado Detalhes',
    detailMeasuredValue: 'Valor Medido(excesso) Detalhes',
    extraAction: 'Expandir Tabela'
  };
  const infractionsColumnOrder = Object.keys(columnLabels);


  const {
    infractionsList,
    isLoading,
    error,
    searchValue,
    setSearchValue,
    handleViewTypeChange,
    viewType,
    handleToggleRow,
    expandedRows,
  } = useInfractionsData({
    externalViewType,
    hasData,
    infractionName
  });

  const {
    showTableSettingsDialog,
    hiddenColumns,
    columnsDetails,
    columnOrder,
    handleToggleTableSettingsDialog,
    handleColumnChange,
    handleColumnDetailsChange,
  } = useTableSettings({
    infractionsColumnOrder,
    columnsDetailsDescriptor,
    columnLabels,
    disabledColumns,
  });

  const menuRef = useRef(document.getElementsByClassName('AssetTree')[0]);
  const lcmCardRef = useRef(document.getElementsByClassName('StatsWidget-wrapper')[0]);
  const { width, screenWidth } = useWindowResize(menuRef, lcmCardRef);

  const columns = columnOrder.filter((name: string) => !hiddenColumns.includes(name) && !subRows.includes(name));
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = infractionsList.map((vehicle: IInfractionResult) => ({ ...withoutHidden(vehicle) })) as IInfractionResult[];

  const searchResult = !searchValue
    ? filteredRows
    : filteredRows.filter((row: Partial<IInfractionResult>) =>
      columns.some((col: string) => {
        const value = row[col as keyof IInfractionResult];
        if (value == null) return false;
        return row[col as keyof IInfractionResult]?.toString().toLowerCase().includes(searchValue.toLowerCase());
      })
    );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;
  const tableClassNames = classNames(
    'table',
    'table-layout-fixed',
    'table-column-overflow-hidden',
    'table-bordered',
    'table-sticky',
    'table-head-filled',
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && 'table-cards table-single-card',
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && 'table-cards table-multi-cards'
  );

  const [showDialog, setShowDialog] = useState(false);
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<number>(0);

  const handleShowMap = (latitude: number, longitude: number) => {
    setLat(latitude);
    setLng(longitude);
    setShowDialog(true);
  };

  return (
    <div>
      <DashboardTableToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        viewType={viewType}
        onViewTypeChange={handleViewTypeChange}
        selectedVehicle={selectedVehicle}
        onToggleSettings={handleToggleTableSettingsDialog}
        showTableSettingsDialog={showTableSettingsDialog}
        onHideSettings={handleToggleTableSettingsDialog}
        onColumnChange={handleColumnChange}
        onColumnDetailsChange={handleColumnDetailsChange}
        columnOrder={columnOrder}
        hiddenColumns={hiddenColumns}
        columnsDetails={columnsDetails}
        columnLabels={columnLabels}
        infractionColumnOrder={infractionsColumnOrder}
        disabledColumns={disabledColumns}
        hasData={!isEmpty(infractionsList)}
      />

      <div
        style={{
          overflowX: screenWidth < 1734 ? 'auto' : undefined,
          maxWidth: screenWidth < 1734 ? width : undefined,
          height: screenWidth < 1734 ? '40vh' : undefined,
        }}
      >
        <DashboardTableBody
          infractionName={infractionName}
          searchResult={searchResult}
          subRows={subRows}
          columns={columns}
          columnLabels={columnLabels}
          columnsDetails={columnsDetails}
          tableClassNames={tableClassNames}
          isLoading={isLoading}
          isViewTypeTable={isViewTypeTable}
          screenWidth={screenWidth}
          width={width}
          expandedRows={expandedRows}
          handleToggleRow={handleToggleRow}
          handleShowMap={handleShowMap}
        />
      </div>

      <Dialog
        show={showDialog}
        body={<MapEvents lat={lat} lng={lng} />}
        title={`Localização do Evento - ${selectedVehicle.name}`}
        onClose={() => setShowDialog(false)}
        showCloseButton
        bsSize='lg'
      />
    </div>
  );
};

export default DashboardTableInfractions;